/**
 * APTableControls Component
 *
 * A comprehensive table controls component following Figma design system specifications.
 * Provides pagination controls and rows per page selection for data tables.
 * Supports customization and accessibility features.
 *
 * @example
 * ```tsx
 * <APTableControls
 *   totalItems={100}
 *   currentPage={2}
 *   itemsPerPage={25}
 *   onPageChange={handlePageChange}
 *   onItemsPerPageChange={handleItemsPerPageChange}
 * />
 *
 * <APTableControls
 *   totalItems={50}
 *   currentPage={1}
 *   itemsPerPage={10}
 *   size="large"
 *   variant="compact"
 *   rowsPerPageOptions={[
 *     { value: 5, label: '5' },
 *     { value: 10, label: '10' },
 *     { value: 20, label: '20' }
 *   ]}
 *   onPageChange={handlePageChange}
 *   onItemsPerPageChange={handleItemsPerPageChange}
 * />
 * ```
 */

import { forwardRef, useCallback, useMemo } from 'react';

import type {
  IAPTableControlsPaginationInfo,
  IAPTableControlsPaginationInfoProps,
  IAPTableControlsPaginationProps,
  IAPTableControlsProps,
  IAPTableControlsRowsPerPageProps,
} from './types';

import './styles.scss';

import APIconButton from '@shared/components/atoms/icon-button';
import APTypography from '@shared/components/atoms/typography';
import APSelect from '@shared/components/molecules/select';
import { useTranslate } from '@shared/hooks/use-translate';

// Default rows per page options
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

// Default pagination info formatter
const defaultPaginationInfoFormatter = (info: IAPTableControlsPaginationInfo): string => {
  if (info.totalItems === 0) {
    return '0 of 0';
  }
  return `${info.startItem}-${info.endItem} of ${info.totalItems}`;
};

// Rows per page selector component
const APTableControlsRowsPerPage = ({
  value,
  options,
  label,
  size,
  disabled,
  onChange,
}: IAPTableControlsRowsPerPageProps) => {
  const handleChange = useCallback(
    (selectedValue: string | number) => {
      onChange(Number(selectedValue));
    },
    [onChange]
  );

  return (
    <div className="ap-table-controls__rows-per-page">
      <APTypography
        as="p"
        size="base"
        weight="400"
        color="muted"
        className="ap-table-controls__rows-per-page-label"
      >
        {label}
      </APTypography>
      <div className="ap-table-controls__rows-per-page-select">
        <APSelect
          size={size}
          value={value}
          options={options.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          onChange={handleChange}
          disabled={disabled}
          data-testid="table-controls-rows-per-page-select"
        />
      </div>
    </div>
  );
};

// Pagination info component
const APTableControlsPaginationInfo = ({
  info,
  formatter = defaultPaginationInfoFormatter,
}: IAPTableControlsPaginationInfoProps) => {
  const formattedInfo = formatter(info);

  return (
    <APTypography
      as="p"
      size="base"
      weight="400"
      color="base"
      className="ap-table-controls__pagination-info"
    >
      {formattedInfo}
    </APTypography>
  );
};

// Pagination buttons component
const APTableControlsPagination = ({
  currentPage,
  totalPages,
  disabled,
  size,
  onPageChange,
}: IAPTableControlsPaginationProps) => {
  const translate = useTranslate();
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handlePreviousPage = useCallback(() => {
    if (canGoPrevious && !disabled) {
      onPageChange(currentPage - 1);
    }
  }, [canGoPrevious, disabled, currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (canGoNext && !disabled) {
      onPageChange(currentPage + 1);
    }
  }, [canGoNext, disabled, currentPage, onPageChange]);

  return (
    <div className="ap-table-controls__pagination-buttons">
      <APIconButton
        size={size === 'large' ? 'medium' : 'small'}
        variant="transparent"
        icon="arrow_left_line"
        aria-label={translate('aria.previous-page')}
        disabled={!canGoPrevious || disabled}
        onClick={handlePreviousPage}
        className="ap-table-controls__pagination-button"
        data-testid="table-controls-previous-page"
      />
      <APIconButton
        size={size === 'large' ? 'medium' : 'small'}
        variant="transparent"
        icon="arrow_right_line"
        aria-label={translate('aria.next-page')}
        disabled={!canGoNext || disabled}
        onClick={handleNextPage}
        className="ap-table-controls__pagination-button"
        data-testid="table-controls-next-page"
      />
    </div>
  );
};

/**
 * APTableControls - Production-ready table controls component
 *
 * Follows AstroPay design system and accessibility guidelines.
 * Based on Figma Table Controls component specifications.
 */
export const APTableControls = forwardRef<HTMLDivElement, IAPTableControlsProps>(
  (
    {
      totalItems,
      currentPage = 1,
      itemsPerPage = 25,
      rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
      size = 'medium',
      variant = 'default',
      disabled = false,
      showRowsPerPage = true,
      showPaginationInfo = true,
      showPaginationButtons = true,
      rowsPerPageLabel = 'Rows per page:',
      className = '',
      'data-testid': testId = 'ap-table-controls',
      onPageChange,
      onItemsPerPageChange,
      formatPaginationInfo,
      leftContent,
      centerContent,
      rightContent,
      ...rest
    },
    ref
  ) => {
    const translate = useTranslate();
    // Calculate pagination info
    const paginationInfo = useMemo<IAPTableControlsPaginationInfo>(() => {
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      // Ensure currentPage doesn't exceed totalPages
      const effectivePage = Math.min(Math.max(1, currentPage), totalPages || 1);
      const startItem = totalItems === 0 ? 0 : (effectivePage - 1) * itemsPerPage + 1;
      const endItem = Math.min(effectivePage * itemsPerPage, totalItems);

      return {
        currentPage: effectivePage,
        totalPages,
        totalItems,
        itemsPerPage,
        startItem,
        endItem,
      };
    }, [totalItems, currentPage, itemsPerPage]);

    // Handle page change
    const handlePageChange = useCallback(
      (page: number) => {
        if (page >= 1 && page <= paginationInfo.totalPages && !disabled) {
          onPageChange?.(page);
        }
      },
      [paginationInfo.totalPages, disabled, onPageChange]
    );

    // Handle items per page change
    const handleItemsPerPageChange = useCallback(
      (newItemsPerPage: number) => {
        if (!disabled) {
          onItemsPerPageChange?.(newItemsPerPage);
          // Reset to first page when changing items per page
          if (currentPage > 1) {
            onPageChange?.(1);
          }
        }
      },
      [disabled, currentPage, onItemsPerPageChange, onPageChange]
    );

    // Construct container class names
    const containerClasses = [
      'ap-table-controls',
      `ap-table-controls--${size}`,
      `ap-table-controls--${variant}`,
      disabled && 'ap-table-controls--disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={ref}
        className={containerClasses}
        data-testid={testId}
        role="toolbar"
        aria-label={translate('aria.table-controls')}
        {...rest}
      >
        {/* Left section - rows per page selector or custom content */}
        <div className="ap-table-controls__left">
          {leftContent ? (
            leftContent
          ) : showRowsPerPage ? (
            <APTableControlsRowsPerPage
              value={itemsPerPage}
              options={rowsPerPageOptions}
              label={rowsPerPageLabel}
              size={size}
              disabled={disabled}
              onChange={handleItemsPerPageChange}
            />
          ) : null}
        </div>

        {/* Center section - custom content */}
        {centerContent && <div className="ap-table-controls__center">{centerContent}</div>}

        {/* Right section - pagination or custom content */}
        <div className="ap-table-controls__right">
          {rightContent ? (
            rightContent
          ) : (
            <div className="ap-table-controls__pagination">
              {/* Pagination info */}
              {showPaginationInfo && (
                <APTableControlsPaginationInfo
                  info={paginationInfo}
                  formatter={formatPaginationInfo}
                />
              )}

              {/* Pagination buttons */}
              {showPaginationButtons && paginationInfo.totalPages > 1 && (
                <APTableControlsPagination
                  currentPage={currentPage}
                  totalPages={paginationInfo.totalPages}
                  disabled={disabled}
                  size={size}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

APTableControls.displayName = 'APTableControls';

export type { IAPTableControlsProps };
export default APTableControls;
